import { type ReactNode, useEffect } from 'react';
import { useKeyboardSafeModal } from '../hooks/useKeyboardSafeModal';

type ConfirmDialogTone = 'primary' | 'warning' | 'danger';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  confirmLabel?: string;
  cancelLabel?: string;
  ariaLabel?: string;
  closeAriaLabel?: string;
  confirmTone?: ConfirmDialogTone;
  isConfirming?: boolean;
}

const CONFIRM_TONE_CLASSES: Record<ConfirmDialogTone, string> = {
  primary:
    'border border-brand-accent/60 bg-brand-primary text-slate-950 shadow-[0_8px_22px_rgba(52,96,136,0.45)] hover:bg-brand-accent',
  warning:
    'border border-amber-500/70 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25',
  danger: 'border border-rose-500/70 bg-rose-500/15 text-rose-100 hover:bg-rose-500/25',
};

export function ConfirmDialog({
  open,
  title,
  message,
  onClose,
  onConfirm,
  confirmLabel = 'Yes',
  cancelLabel = 'No',
  ariaLabel = `${title} confirmation`,
  closeAriaLabel,
  confirmTone = 'primary',
  isConfirming = false,
}: ConfirmDialogProps) {
  const { dialogAutoFocus, dialogRef, isKeyboardSafeLayout, viewportStyle } = useKeyboardSafeModal(open);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  const resolvedCloseAriaLabel = closeAriaLabel ?? `Close ${title.toLowerCase()} confirmation`;

  return (
    <div
      className={`fixed inset-0 z-[80] flex bg-slate-950/65 backdrop-blur-sm ${
        isKeyboardSafeLayout ? 'items-start justify-stretch p-3 sm:p-4' : 'items-center justify-center p-4'
      }`}
      style={viewportStyle}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={`w-full border border-border-subtle bg-surface-elevated shadow-[0_24px_54px_rgba(2,8,24,0.62)] ring-1 ring-slate-100/5 ${
          isKeyboardSafeLayout
            ? 'flex max-h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl'
            : 'max-w-sm rounded-2xl'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isConfirming}
            className="rounded p-1 text-slate-400 transition-colors hover:bg-surface-panel/70 hover:text-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={resolvedCloseAriaLabel}
          >
            X
          </button>
        </div>

        <div className="px-4 py-4 text-sm leading-6 text-slate-300">{message}</div>

        <div className="flex justify-end gap-2 border-t border-border-subtle px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            autoFocus={dialogAutoFocus}
            disabled={isConfirming}
            className="rounded-md border border-border-subtle bg-surface-panel/75 px-4 py-2 text-sm font-semibold text-slate-100 transition-colors hover:border-brand-primary/45 hover:bg-surface-panel disabled:cursor-not-allowed disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              void onConfirm();
            }}
            disabled={isConfirming}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${CONFIRM_TONE_CLASSES[confirmTone]}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
